import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { store } from "../store";
import type {
	CanvasRuntimeFactory,
	CanvasRuntimeKey,
	CanvasRuntimeParams,
	Disposable,
} from "./canvas-runtime.types";

const logger = new Logger("canvas-runtime");

// ============================================================================
// Factory Registry
// ============================================================================

/**
 * Holds the registered canvas runtime factory function.
 *
 * This is set once at app initialization from packages/app.
 */
interface CanvasRuntimeFactoryRegistry {
	factory: CanvasRuntimeFactory<Disposable> | null;
}

const $canvasRuntimeFactory = atom<CanvasRuntimeFactoryRegistry>({ factory: null });
$canvasRuntimeFactory.debugLabel = "canvasRuntimeFactory";
$canvasRuntimeFactory.debugPrivate = true;

/**
 * Register the canvas runtime factory function.
 *
 * Called once from packages/app during initialization.
 */
export const registerCanvasRuntimeFactory = <T extends Disposable>(
	factory: CanvasRuntimeFactory<T>
): void => {
	if (typeof factory !== "function") {
		throw new Error("Canvas runtime factory must be a function");
	}
	store.set($canvasRuntimeFactory, { factory: factory as CanvasRuntimeFactory<Disposable> });
	logger.debug("Canvas runtime factory registered");
};

// ============================================================================
// Runtime Storage
// ============================================================================

/**
 * Stores the canvas runtime instance for a document/page.
 *
 * Using atomFamily with a custom comparator (isEqual) ensures that we get the same
 * atom instance for equivalent keys. When the atom is no longer in use (no subscribers),
 * Jotai will garbage collect it, and we dispose the runtime in the onMount cleanup.
 *
 * This solves the memory leak issue where runtimes were stored in a module-level Map
 * that was never cleaned up.
 */
export const $canvasRuntime = atomFamily(({ documentId, pageId }: CanvasRuntimeKey) => {
	// Store the runtime instance and its params
	const runtimeAtom = atom(null as Disposable | null);
	runtimeAtom.debugLabel = `canvasRuntime/${documentId}/${pageId}`;

	// Create a wrapper atom that handles initialization and cleanup
	const managedRuntimeAtom = atom(
		(get) => get(runtimeAtom),
		(
			get,
			set,
			params: CanvasRuntimeParams | { action: "dispose" } | { action: "initialize" }
		) => {
			// Handle dispose action
			if ("action" in params && params.action === "dispose") {
				const runtime = get(runtimeAtom);
				if (runtime) {
					logger.debug("Disposing canvas runtime", { documentId, pageId });
					runtime.dispose();
					set(runtimeAtom, null);
				}
				return;
			}

			// Handle initialize action - only creates if not already created
			if ("action" in params && params.action === "initialize") {
				// Already initialized, nothing to do
				return;
			}

			// Handle initialization with params
			const existing = get(runtimeAtom);
			if (existing) {
				// Runtime already exists, return it
				return;
			}

			const { factory } = get($canvasRuntimeFactory);
			if (!factory) {
				logger.error("No canvas runtime factory registered", { documentId, pageId });
				return;
			}

			logger.debug("Creating canvas runtime", { documentId, pageId });
			const runtime = factory(params as CanvasRuntimeParams);
			set(runtimeAtom, runtime);
		}
	);
	managedRuntimeAtom.debugLabel = `canvasRuntime/managed/${documentId}/${pageId}`;

	// Add onMount to handle cleanup when atom is garbage collected
	managedRuntimeAtom.onMount = (setAtom) => {
		// Return cleanup function that disposes the runtime
		return () => {
			setAtom({ action: "dispose" });
		};
	};

	return managedRuntimeAtom;
}, isEqual);

/**
 * Get an existing canvas runtime without creating a new one.
 *
 * This is useful for checking if a runtime already exists without triggering creation.
 */
export const getCanvasRuntime = (key: CanvasRuntimeKey): Disposable | null => {
	return store.get($canvasRuntime(key));
};

/**
 * Manually dispose a canvas runtime.
 *
 * This is useful when you need to explicitly clean up a runtime before
 * the atom is garbage collected.
 */
export const disposeCanvasRuntime = (key: CanvasRuntimeKey): void => {
	store.set($canvasRuntime(key), { action: "dispose" });
};
